import { ActionIcon, Group, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconClipboard, IconDownload, IconEye, IconTrash } from '@tabler/icons-react';
import { useRef, useState } from 'react';

import { api, apiOrigin } from '../api';
import { requestFileDownload } from '../services/file-transfer';
import { apiErrorMessage, responseErrorMessage } from '../utils/api-error';

const maximumClipboardBytes = 10 * 1024 * 1024;

type FileActionsProps = {
  fileId: string;
  name: string;
  mimeType: string | null;
  size: number;
  onDeleteStarted: () => void | Promise<void>;
  onDeleteFailed: () => void | Promise<void>;
  onDeleted: () => void | Promise<void>;
};

export function FileActions({
  fileId,
  name,
  mimeType,
  size,
  onDeleteStarted,
  onDeleteFailed,
  onDeleted,
}: FileActionsProps) {
  const [downloading, setDownloading] = useState(false);
  const [opening, setOpening] = useState(false);
  const [copying, setCopying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deleteInFlight = useRef(false);
  const clipboardType = mimeType?.startsWith('text/')
    ? 'text/plain'
    : mimeType?.startsWith('image/')
      ? mimeType
      : null;
  const canCopy = size < maximumClipboardBytes && clipboardType !== null;

  async function download() {
    setDownloading(true);
    try {
      const ticket = await requestFileDownload(fileId);
      const anchor = document.createElement('a');
      anchor.href = ticket.url;
      anchor.download = name;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch (error) {
      notifications.show({
        color: 'red',
        title: `Download failed: ${name}`,
        message: apiErrorMessage(error, { fallback: 'Could not start the download.' }),
      });
    } finally {
      setDownloading(false);
    }
  }

  async function copyContents() {
    setCopying(true);
    try {
      if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined' || !clipboardType) {
        throw new Error('This file type cannot be copied by Brave.');
      }
      if (ClipboardItem.supports && !ClipboardItem.supports(clipboardType)) {
        throw new Error(
          `Brave cannot copy ${clipboardType} without converting it. The file was not changed.`,
        );
      }

      // Clipboard permission requires user activation. Start the write immediately and let
      // Chromium resolve the promised content while the ticket and file are being fetched.
      const clipboardContent = (async () => {
        const ticket = await requestFileDownload(fileId);
        const response = await fetch(ticket.url);
        if (!response.ok) throw new Error('Could not read the file.');
        const blob = await response.blob();
        if (clipboardType === 'text/plain') {
          return new Blob([await blob.text()], { type: clipboardType });
        }
        return new Blob([await blob.arrayBuffer()], { type: clipboardType });
      })();
      await navigator.clipboard.write([new ClipboardItem({ [clipboardType]: clipboardContent })]);
      notifications.show({ color: 'green', message: `${name} copied to the clipboard.` });
    } catch (error) {
      const unsupportedSvg = clipboardType === 'image/svg+xml' && error instanceof DOMException;
      notifications.show({
        color: 'red',
        message: unsupportedSvg
          ? 'Brave cannot copy SVG as native SVG clipboard data. The file was not changed.'
          : error instanceof Error
            ? error.message
            : 'Could not copy the file.',
      });
    } finally {
      setCopying(false);
    }
  }

  async function openPdf() {
    const previewWindow = window.open('about:blank', '_blank');
    setOpening(true);
    try {
      const response = await api.api.api.v1.files({ id: fileId })['preview-ticket'].post();
      if (response.error || !response.data) {
        throw new Error(
          apiErrorMessage(response.error, {
            fallback: `Could not open “${name}”.`,
            status: response.status,
          }),
        );
      }
      const previewUrl = `${apiOrigin}${response.data.path}`;
      const readiness = await fetch(previewUrl, { method: 'HEAD', cache: 'no-store' });
      if (!readiness.ok) {
        throw new Error(
          await responseErrorMessage(readiness, `The preview for “${name}” cannot be opened.`),
        );
      }
      if (previewWindow) {
        previewWindow.opener = null;
        previewWindow.location.href = previewUrl;
      } else {
        notifications.show({
          color: 'red',
          title: `Preview blocked: ${name}`,
          message: 'Allow pop-ups for this site, then try opening the PDF again.',
        });
      }
    } catch (error) {
      previewWindow?.close();
      notifications.show({
        color: 'red',
        title: `Preview failed: ${name}`,
        message: apiErrorMessage(error, { fallback: `Could not open “${name}”.` }),
      });
    } finally {
      setOpening(false);
    }
  }

  function confirmDelete() {
    modals.openConfirmModal({
      title: 'Delete file?',
      children: `“${name}” will be permanently deleted. This cannot be undone.`,
      labels: { confirm: 'Delete file', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        if (deleteInFlight.current) return;
        deleteInFlight.current = true;
        setDeleting(true);
        try {
          await onDeleteStarted();
          const response = await api.api.api.v1.files({ id: fileId }).delete();
          if (response.error && response.status !== 404) {
            await onDeleteFailed();
            notifications.show({
              color: 'red',
              title: `Delete failed: ${name}`,
              message: apiErrorMessage(response.error, {
                fallback: `“${name}” could not be deleted.`,
                status: response.status,
              }),
            });
            return;
          }
          void onDeleted();
          notifications.show({ color: 'green', message: `${name} deleted.` });
        } catch (error) {
          await onDeleteFailed();
          notifications.show({
            color: 'red',
            title: `Delete failed: ${name}`,
            message: apiErrorMessage(error, { fallback: `“${name}” could not be deleted.` }),
          });
        } finally {
          deleteInFlight.current = false;
          setDeleting(false);
        }
      },
    });
  }

  return (
    <Group gap={4} wrap='nowrap'>
      {mimeType === 'application/pdf' && (
        <Tooltip label='View PDF'>
          <ActionIcon
            variant='subtle'
            aria-label={`View ${name}`}
            loading={opening}
            onClick={() => void openPdf()}
          >
            <IconEye size={18} />
          </ActionIcon>
        </Tooltip>
      )}
      {canCopy && (
        <Tooltip label='Copy contents'>
          <ActionIcon
            variant='subtle'
            aria-label={`Copy ${name}`}
            loading={copying}
            onClick={() => void copyContents()}
          >
            <IconClipboard size={18} />
          </ActionIcon>
        </Tooltip>
      )}
      <Tooltip label='Download'>
        <ActionIcon
          variant='subtle'
          aria-label={`Download ${name}`}
          loading={downloading}
          onClick={() => void download()}
        >
          <IconDownload size={18} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label='Delete file'>
        <ActionIcon
          variant='subtle'
          color='red'
          aria-label={`Delete ${name}`}
          loading={deleting}
          onClick={confirmDelete}
        >
          <IconTrash size={18} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}
